import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import { useAccountInfo, useIsLogin } from 'store/account/hooks'
import { useHideOtherPairs } from 'store/orderbook/hooks'
import { useCurrentSpotSymbol, useSpotMatchClearings } from 'store/spot/hooks'
import { HistoryOrdersWrap, TradeHistoryTxHash, SymbolLink } from '../styles'
import { TableList } from 'components/TableList'
import { Equal } from 'utils/equal'
import useWindow from 'hooks/useWindow'
import { DateFormatter } from 'utils/datetime'
import { SpotMatchRecord, Role } from 'api/v1/spot'
import { useSpotSymbols } from 'store/spot/hooks'
import { findSymbolByName } from 'utils/symbols'
import { ReactComponent as ArrowIconSvg } from 'assets/orders/symbol_arrow.svg'
import { useMarketNavigate } from 'hooks/useMarketNavigate'
import { TrendColorizer } from 'components/TrendColorizer'
import { OrderDirection } from 'store/spot/types'
import { getEffectiveDecimals, toFixed } from 'utils/number'
import { encryptionAddress } from 'utils/address'
import { viewOnZKLinkExplorer } from 'utils/explorer'
import { getSpotMatchClearingsAction } from '../../../../store/spot/actions/getSpotMatchClearingsActions'
import { calcOrderValue } from '../../../../hooks/useSizeCalculations'
import { SideSelectPopup } from '../components/SideSelectPopup'
import { SpotOrderDirectionFilterEnum } from 'store/spot/types'
import MobileRowItem from './MobileRowItem'
import MobileTableList from 'components/TableList/MobileTableList'

export const TradeHistoryView = memo(() => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const hideOtherPairs = useHideOtherPairs()
  const isLogin = useIsLogin()
  const { accountId = 0 } = useAccountInfo()
  const currentSpotSymbol = useCurrentSpotSymbol()
  const { name } = currentSpotSymbol ?? {}
  const spotSymbols = useSpotSymbols()
  const { navigateToSpot } = useMarketNavigate()
  const { isMobile } = useWindow()
  const { list, isLastPage, offsetId } = useSpotMatchClearings()
  // const [filterSide, setFilterSide] = useState(SpotOrderDiectionFilterEnum.All)
  const [fetchParams, setFetchParams] = useState<any>({
    direction: SpotOrderDirectionFilterEnum.All,
    offsetId: 0,
  })

  // useEffect(() => {
  //   if (isLogin && !list?.length) {
  //     dispatch(
  //       getSpotMatchClearingsAction({
  //         accountId,
  //         body: {
  //           offsetId: 0,
  //         },
  //       }),
  //     )
  //   }
  // }, [dispatch, isLogin, accountId, list])

  useEffect(() => {
    if (isLogin) {
      dispatch(
        getSpotMatchClearingsAction({
          accountId,
          body: {
            ...fetchParams,
          },
        }),
      )
    }
  }, [dispatch, fetchParams, isLogin, accountId])

  const renderList = useMemo(() => {
    if (hideOtherPairs && !!name)
      return list.filter((v) => Equal.upperCase(v.symbol, name))
    return list
  }, [list, hideOtherPairs, name])

  const getSymbolInfo = (symbolName: string) => {
    const symbol = findSymbolByName(spotSymbols, symbolName)
    const {
      baseCurrency = '',
      quoteCurrency = '',
      baseScale = 0,
      quoteScale = 0,
    } = symbol ?? {}

    return {
      baseCurrency,
      quoteCurrency,
      baseScale,
      quoteScale,
    }
  }

  const getFixedQuantity = (row: SpotMatchRecord) => {
    const { matchQuantity = 0, symbol } = row
    const { baseScale } = getSymbolInfo(symbol)
    return toFixed(matchQuantity, baseScale)
  }

  const getFixedPrice = (row: SpotMatchRecord) => {
    const { matchPrice = 0, symbol } = row
    const { quoteScale } = getSymbolInfo(symbol)
    return toFixed(matchPrice, quoteScale)
  }

  const getFixedValue = (row: SpotMatchRecord) => {
    const { matchPrice = 0, symbol, matchQuantity = 0 } = row
    const { quoteScale } = getSymbolInfo(symbol)
    const value = calcOrderValue(String(matchQuantity), matchPrice)

    return toFixed(value, quoteScale)
  }

  const onSelectDirectionFilter = (value: SpotOrderDirectionFilterEnum) => {
    setFetchParams((prev: any) => {
      return Object.assign({}, { ...prev }, { direction: value })
    })
  }

  const filterRenderList = useMemo(() => {
    if (renderList?.length > 0) {
      let list = renderList
      const { direction } = fetchParams

      if (direction !== SpotOrderDirectionFilterEnum.All) {
        list = list.filter((d) => d.direction === direction)
      }
      return list
    }
    return renderList
  }, [renderList, fetchParams])

  const columns = [
    {
      key: 'date',
      title: 'Date',
      render: (row: SpotMatchRecord) => {
        const { createdAt } = row
        const datetime = DateFormatter.toISODateTime(createdAt)
        return <span style={{ color: '#fff' }}>{datetime}</span>
      },
      width: '13%',
    },
    {
      key: 'symbol',
      title: 'Pair',
      render: (row: SpotMatchRecord) => {
        const { symbol } = row
        const { baseCurrency, quoteCurrency } = getSymbolInfo(symbol)

        return (
          <SymbolLink
            sx={{ cursor: 'pointer' }}
            onClick={() => navigateToSpot(baseCurrency, quoteCurrency)}
          >
            {baseCurrency}/{quoteCurrency}
            <ArrowIconSvg />
          </SymbolLink>
        )
      },
      width: '13%',
    },
    {
      key: 'side',
      // title: t('user-order-table-header-side'),
      titleRender: () => {
        return (
          <SideSelectPopup
            onSelected={onSelectDirectionFilter}
            filterSide={fetchParams?.direction}
          />
        )
      },
      render: (row: SpotMatchRecord) => {
        const { direction } = row
        return (
          <TrendColorizer
            trend={direction === OrderDirection.LONG}
            value={
              direction === OrderDirection.LONG
                ? t('user-order-table-content-buy')
                : t('user-order-table-content-sell')
            }
          />
        )
      },
      width: '10%',
    },
    {
      key: 'size',
      title: t('user-order-table-header-size'),
      render: (row: SpotMatchRecord) => {
        const { symbol } = row
        const fixedQuantity = getFixedQuantity(row)
        const { baseCurrency } = getSymbolInfo(symbol)
        return <>{`${t(fixedQuantity)} ${baseCurrency}`}</>
      },
      width: '12%',
    },
    {
      key: 'price',
      title: t('user-order-table-header-price'),
      render: (row: SpotMatchRecord) => {
        const { symbol } = row
        const fixedPrice = getFixedPrice(row)
        const { quoteCurrency } = getSymbolInfo(symbol)
        return <>{`${t(fixedPrice)} ${quoteCurrency}`}</>
      },
      width: '12%',
    },
    {
      key: 'fee',
      title: t('user-order-table-header-fee'),
      render: (row: SpotMatchRecord) => {
        const { feeCurrency = '', fee = 0 } = row
        const effectiveFee = getEffectiveDecimals(fee)
        return <>{`${t(effectiveFee)} ${feeCurrency}`}</>
      },
      width: '12%',
    },
    {
      key: 'value',
      title: 'Total',
      // title: t('user-order-table-header-value'),
      render: (row: SpotMatchRecord) => {
        const { symbol } = row
        const fixedValue = getFixedValue(row)
        const { quoteCurrency } = getSymbolInfo(symbol)
        return <>{`${t(fixedValue)} ${quoteCurrency}`}</>
      },
      width: '12%',
    },
    {
      key: 'role',
      title: 'Role',
      render: (row: SpotMatchRecord) => {
        const { type } = row
        return <>{type === Role.MAKER ? 'Maker' : 'Taker'}</>
      },
      width: '8%',
    },
    {
      key: 'hash',
      title: t('user-order-table-header-tx-hash'),
      render: (row: SpotMatchRecord) => {
        const { txHash } = row
        return (
          <TradeHistoryTxHash
            style={{ paddingLeft: '0px' }}
            className="content"
            onClick={() => viewOnZKLinkExplorer(txHash)}
          >
            {txHash ? encryptionAddress(txHash) : '-'}
          </TradeHistoryTxHash>
        )
      },
      width: '10%',
    },
  ]

  const onScrollFetch = () => {
    if (isLogin) {
      dispatch(
        getSpotMatchClearingsAction({
          accountId,
          body: {
            ...fetchParams,
            offsetId: offsetId || 0,
          },
        }),
      )
    }
  }

  return (
    <HistoryOrdersWrap>
      {isMobile ? (
        <MobileTableList
          dataSource={filterRenderList}
          hasMore={!isLastPage}
          onScrollEnd={() => {
            if (isLastPage) return
            onScrollFetch()
          }}
          renderItem={(row) => {
            return <MobileRowItem row={row}></MobileRowItem>
          }}
        />
      ) : (
        <TableList
          hasMore={!isLastPage}
          onScrollEnd={() => {
            if (isLastPage) return
            onScrollFetch()
          }}
          columns={columns}
          dataSource={filterRenderList}
        />
      )}
    </HistoryOrdersWrap>
  )
})
